import { Component, OnInit, Optional, Inject, ViewChild } from '@angular/core'; 
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MediaObserver } from '@angular/flex-layout';
import { DialogService, LanguageService } from 'src/app/services';
import { of as observableOf, fromEvent, Subject, Subscription } from 'rxjs';
import { finalize, catchError, tap, map, takeUntil, debounceTime, filter, distinctUntilChanged } from 'rxjs/operators';
import { RecipientMaintenanceService } from '../recipient-maintenance.service';
import { environment } from 'src/environments/environment';
import * as _ from 'lodash';

import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipList } from '@angular/material/chips';

@Component({
  selector: 'esc-recipient-maintenance-filter',
  templateUrl: './recipient-maintenance-filter.component.html',
  styleUrls: ['./recipient-maintenance-filter.component.scss']
})
export class RecipientMaintenanceFilterComponent implements OnInit {

  constructor(
    private service: RecipientMaintenanceService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    private fb: FormBuilder,
    public media: MediaObserver,
    public dialogRef: MatDialogRef<RecipientMaintenanceFilterComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  @ViewChild('chipsWebUser') chipsWebUser: MatChipList;
  @ViewChild('webuserAC') webuserACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) webuserACTr: MatAutocompleteTrigger;
  
  public loading = {
    status: false,
    error: false,
    webuser: false
  };

  public networkErrMsg = this.languageService.getTranslation("MESSAGE_ERROR_NETWORK_CONNECTION", {});

  public statuses = [
    {
      code: null,
      description: "All",
    },
    {
      code: 1,
      description: "Active",
    },
    {
      code: 0,
      description: "Inactive",
    },
  ];

  public defaultOption = {
    id: 0,
    code: 'all',
    description: 'All'
  };

  public filterOptions = {
    webuser: []
  };

  public multiSelect = {
    selectedWebUsers: [this.defaultOption]
  };

  public filters;
  public formGroup: FormGroup;
  private webuserSub: Subscription;

  ngOnInit() {
    this.filters = _.cloneDeep(INITIAL_OPTIONS);
    this.initForm();
  }

  ngOnDestroy(): void {
    this.webuserSub.unsubscribe();
  }

  initForm() {
    this.formGroup = this.fb.group({
      status: [_.get(this.data, 'status') || this.statuses[0], Validators.required],
      webuser: [{value: this.defaultOption},  Validators.required]
    });

    // Set web users from previous filters if exists
    let selectedWebUsers = _.get(this.data, 'webuser');
    if (selectedWebUsers) {
      this.formGroup.controls['webuser'].setValue(selectedWebUsers);
      this.multiSelect.selectedWebUsers = selectedWebUsers;
    }

    this.subscribeFormChanges();
  }
  
  compareWithFunc = (a: any, b: any) => (a && b) ? a.code == b.code : null;

  subscribeFormChanges(): void {
    this.webuserSub = this.formGroup.controls["webuser"].valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term))
    )
    .subscribe(value => {
      this.filterOptions.webuser = []
      this.filters.web_filter.keyword = value;
      this.getData('webuser')
    });
  }

  toggleSelection(data, srcArray, targetArray, formControlName, inputElem) {
    if (inputElem) {
      inputElem.value = '';
    }
    data.selected = !data.selected;

    //if 'all' is selected, choose only all
    if (data.selected == true && data.code == 'all') {
      srcArray.forEach((e) => {
        e.selected = true;
      });
      this.multiSelect[targetArray] = [];
      this.multiSelect[targetArray].push(data);
      this.disableCheckbox(srcArray, formControlName, data);
    }

    //if 'all' is deselected, unselect everything
    else if (data.selected == false && data.code == 'all') {
      srcArray.forEach((e) => {
        e.selected = false;
      });
      this.multiSelect[targetArray].splice(
        this.multiSelect[targetArray].indexOf(data),
        1
      );
      this.enableCheckbox(srcArray);
    } else if (this.multiSelect[targetArray].indexOf(data) == -1) {
      //if other option is selected, check if 'all' is already selected and clear selection
      // this.multiSelect[targetArray].some(opt => opt.code == data.code)
      if (this.multiSelect[targetArray].indexOf(this.defaultOption) > -1) {
        srcArray.forEach((e) => {
          e.selected = false;
        });
        this.multiSelect[targetArray] = [];
      }
      //if data exists in array, remove from list
      else if (
        this.multiSelect[targetArray].some((opt) => opt.code == data.code)
      ) {
        let index = _.findIndex(this.multiSelect[targetArray], function (item) {
          return item['code'] == data.code;
        });
        this.multiSelect[targetArray].splice(index, 1);
      } else {
        this.multiSelect[targetArray].push(data);
      }
    } else {
      this.multiSelect[targetArray].splice(
        this.multiSelect[targetArray].indexOf(data),
        1
      );
      srcArray.forEach((e) => {
        if (e.id == data.id) {
          e.selected = false;
        }
      });
    }


    this.assignFormValues(targetArray, formControlName);
  }

  getInitialData(name) {
    this.filters = _.cloneDeep(INITIAL_OPTIONS)
    this.autoCmpltClosed(name);
    this.getData(name);
  }

  autoCmpltClosed(name) {
    this.filterOptions[name] = [];
  }

  handleFilterResponse(res, filterName, filters, selectedArrayGroup) {
    this.loading[filterName] = false;
    if (environment.showLogs) {
      console.log(res);
    }
    if (
      res == null ||
      res.status == 0 ||
      res.status == 504 ||
      res.status == 500
    ) {
      this.loading.error = true;
      this.dialogService.openSnackBar(this.networkErrMsg);
      return;
    }
    if (_.has(res, 'value') && res.value == 'Success') {
      this.filterOptions[filterName] = this.filterOptions[filterName].concat(
        res.data
      );
      this.checkSelected(filterName, selectedArrayGroup, filterName);

      if (
        res.data.length > 0 &&
        this.filterOptions[filterName].length < _.get(res, 'count')
      ) {
        this.filters.getMore = true;
      }
    }
  }

  checkAutocompleteObject(control, objectKey, targetArray) {
    if (
      (!_.isObject(control.controls[objectKey].value) ||
        control.controls[objectKey].value.id == null) &&
      control.controls[objectKey].value != ''
    ) {
      control.controls[objectKey].setErrors({
        notExisting: true
      });
    }

    if (_.size(this[targetArray]) && targetArray) {
      control.controls[objectKey].setValue(null);
      this.assignFormValues(targetArray, objectKey);
    }
  }

  displayFn(data?: any): string | undefined {
    return data ? _.get(data, 'code') + ' - ' + _.get(data, 'description') : null;
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map((x) => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe((x) => {
            const scrollTop = this[refName].panel.nativeElement.scrollTop;
            const scrollHeight = this[refName].panel.nativeElement.scrollHeight;
            const elementHeight =
              this[refName].panel.nativeElement.clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  getMoreData(name) {
    if (this.filters.getMore) {
      this.filters.web_filter.offset += INITIAL_OPTIONS.web_filter.limit;
      this.getData(name);
    }
  }

  optionClicked(
    event: Event,
    data,
    srcArray,
    targetArray,
    formControlName,
    inputElem
  ) {
    event.stopPropagation();
    this.toggleSelection(
      data,
      srcArray,
      targetArray,
      formControlName,
      inputElem
    );
  }
  
  checkSelected(srcArray, targetArray, formControlName) {
    if (this.multiSelect[targetArray].some((opt) => opt.description == 'All')) {
      let tempObj = _.cloneDeep(this.defaultOption);
      tempObj['selected'] = false;
      this.toggleSelection(
        tempObj,
        this.filterOptions[srcArray],
        targetArray,
        formControlName,
        null
      );
    } else if (this.multiSelect[targetArray].length) {
      this.filterOptions[srcArray].forEach((e) => {
        this.multiSelect[targetArray].forEach((v) => {
          if (e.description == v.description) {
            e.selected = v.selected;
          }
        });
        if (e.description == 'All') {
          e.selected = false;
        }
      });
    } else {
      this[srcArray].forEach((e) => {
        e.selected = false;
      });
    }
  }

  getData(name) {
    let filters = _.cloneDeep(this.filters);
    let formValue = this.formGroup.value

    this.filters.getMore = false;
    this.loading[name] = true;
    switch (name) {
      case 'webuser': {
        this.filterOptions.webuser = [];
        this.service
          .getWebUserList(filters)
          .pipe(
            tap(() => (this.loading.webuser = true)),
            finalize(() => {
              this.loading.webuser = false;
              return observableOf(null);
            }),
            catchError((err) => {
              console.error(err);
              this.loading.webuser = false;
              return observableOf(null);
            })
          )
          .subscribe((res: any) => {
            this.filterOptions[name][0] = this.defaultOption;
            this.handleFilterResponse(
              {
                ...res,
                data: _.map(res.data, val => {
                    return {
                    "id": val.id,
                    "code": val.username,
                    "description": val.fullname
                  }
                })
              },
              'webuser',
              filters,
              'selectedWebUsers'
            );
          });
        break;
      }
    }
  }

  enableCheckbox(optionArray) {
    optionArray.forEach((e) => {
      e.disabled = false;
    });
  }

  disableCheckbox(optionArray, formControlName, data) {
    if (
      (optionArray[0].selected &&
        _.has(this.formGroup.get(formControlName), 'value') &&
        this.formGroup.get(formControlName).value == 0) ||
      _.get(data, 'code') == 'all'
    ) {
      optionArray.forEach((e) => {
        if (e.code != 'all') {
          e.disabled = true;
        }
      });
    } else {
      this.enableCheckbox(optionArray);
    }
  }

  assignFormValues(targetArray, formControlName) {
    let tempArr;
    tempArr = this.multiSelect[targetArray].map((e) => {
      return e;
    });
    let val = _.cloneDeep(tempArr);
    this.formGroup.controls[formControlName].patchValue(val);
  }

  resetControl(control: FormControl) {
    control.reset(null, { emitEvent: false });
  }

  onFilterClose(name, input) {
    if (!_.isObject(this.formGroup.get(name).value)) {
      input.value = "";
    }
  }

  applyFilters() {
    for (let ctrl in this.formGroup.controls) {
      if(!this.formGroup.controls[ctrl].valid) {
        this.formGroup.controls[ctrl].markAsTouched();
      }
    }

    if(this.formGroup.valid) {
      this.dialogRef.close(this.formGroup.value);
    }
  }

  reset() {
    for(let ctrl in this.formGroup.controls) {
      this.formGroup.controls[ctrl].reset(null, {emitEvent: false});
      this.formGroup.controls[ctrl].markAsUntouched();
    }

    // Set default values
    this.formGroup.get('status').setValue(this.statuses[0]);

    let defaultWebUser = [this.defaultOption];
    this.formGroup.get('webuser').setValue(defaultWebUser);
    this.multiSelect.selectedWebUsers = defaultWebUser;
  }
}

const INITIAL_OPTIONS = {
	web_filter: {
		limit: 50,
		offset: 0,
    keyword: "",
    order_by: "asc",
		is_asc: 1
	}
}